import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Bell,
  AtSign,
  MessageSquare,
  Smile,
  FileText,
  Search,
  ArrowRight,
  Inbox,
  Paperclip,
  Send,
  Reply,
  UserPlus,
  AlertTriangle,
  LayoutGrid,
  Check,
  X,
  FileCode,
  Image as ImageIcon,
  Film,
  Music,
  Archive,
} from 'lucide-react';
import type { Message, WorkspaceFile, Notification, DirectMessage, DirectConversation, Channel } from '@sahaj/types';
import { parseContentSegments } from '@sahaj/utils';
import { UserAvatar } from '../common/UserAvatar';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useChat } from '../../context/ChatContext';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../hooks/useLanguage';
import { api } from '../../lib/api';

type ActivityFilter = 'ALL' | 'MENTIONS' | 'REPLIES' | 'REACTIONS' | 'DMS' | 'FILES';

interface ActivityViewProps {
  onNavigateToContext?: (type: 'channel' | 'dm' | 'files', id?: string) => void;
}

export type ActivityEventType =
  | 'MESSAGE'
  | 'REPLY'
  | 'MENTION'
  | 'REACTION'
  | 'FILE_SHARED'
  | 'DM'
  | 'MEMBER_ADDED'
  | 'EMERGENCY_ALERT'
  | 'GENERIC';

export interface FeedActivityItem {
  id: string;
  type: ActivityEventType;
  actor: {
    id: string;
    displayName: string;
    avatarUrl?: string | undefined;
  };
  actionText: string;
  channelName?: string | undefined;
  channelId?: string | undefined;
  conversationId?: string | undefined;
  participantName?: string | undefined;
  content?: string | undefined;
  fileInfo?: {
    fileName: string;
    fileSize: string;
    iconType: 'figma' | 'pdf' | 'image' | 'code' | 'video' | 'audio' | 'archive' | 'doc';
  } | undefined;
  reactionEmoji?: string | undefined;
  isRead: boolean;
  createdAt: string;
  notificationId?: string | undefined;
}

// ─── Pastel Channel Badges ────────────────────────────────────────────────────
const CHANNEL_BADGE_PALETTES: string[] = [
  'bg-purple-50 text-purple-700 dark:bg-purple-950/60 dark:text-purple-300 border-purple-200/60 dark:border-purple-900/40',
  'bg-pink-50 text-pink-700 dark:bg-pink-950/60 dark:text-pink-300 border-pink-200/60 dark:border-pink-900/40',
  'bg-blue-50 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300 border-blue-200/60 dark:border-blue-900/40',
  'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 border-emerald-200/60 dark:border-emerald-900/40',
  'bg-amber-50 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300 border-amber-200/60 dark:border-amber-900/40',
  'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-300 border-indigo-200/60 dark:border-indigo-900/40',
  'bg-teal-50 text-teal-700 dark:bg-teal-950/60 dark:text-teal-300 border-teal-200/60 dark:border-teal-900/40',
];

function getChannelBadgeClass(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash << 5) - hash + name.charCodeAt(i);
    hash |= 0;
  }
  const idx = Math.abs(hash) % CHANNEL_BADGE_PALETTES.length;
  return CHANNEL_BADGE_PALETTES[idx] ?? CHANNEL_BADGE_PALETTES[0] ?? 'bg-purple-50 text-purple-700 border-purple-200';
}

const ChannelBadge: React.FC<{ name: string }> = ({ name }) => (
  <span className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded-md text-[11px] font-semibold border ${getChannelBadgeClass(name)}`}>
    # {name}
  </span>
);

// ─── File Formatting & Logos ──────────────────────────────────────────────────
function formatBytes(bytes?: number): string {
  if (!bytes || bytes <= 0) return '1.8 MB';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function getFileDetails(fileName: string, fileSizeNum?: number, rawPreview?: string) {
  const ext = fileName.includes('.') ? fileName.split('.').pop()?.toLowerCase() || '' : '';

  let fileSize = formatBytes(fileSizeNum);
  if (rawPreview) {
    const sizeMatch = rawPreview.match(/(\d+(?:\.\d+)?\s*(?:KB|MB|GB|B))/i);
    if (sizeMatch && sizeMatch[1]) {
      fileSize = sizeMatch[1];
    }
  }

  let iconType: 'figma' | 'pdf' | 'image' | 'code' | 'video' | 'audio' | 'archive' | 'doc' = 'doc';
  if (ext === 'fig') iconType = 'figma';
  else if (ext === 'pdf') iconType = 'pdf';
  else if (['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'avif'].includes(ext)) iconType = 'image';
  else if (['ts', 'tsx', 'js', 'jsx', 'json', 'html', 'css', 'java', 'py', 'sql', 'cpp', 'rs'].includes(ext)) iconType = 'code';
  else if (['mp4', 'mov', 'webm', 'mkv', 'avi'].includes(ext)) iconType = 'video';
  else if (['mp3', 'wav', 'ogg', 'm4a', 'aac'].includes(ext)) iconType = 'audio';
  else if (['zip', 'tar', 'gz', 'rar', '7z'].includes(ext)) iconType = 'archive';

  return { fileName, ext, fileSize, iconType };
}

const FigmaLogo: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg className={className} viewBox="0 0 38 57" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 28.5C19 23.2533 23.2533 19 28.5 19C33.7467 19 38 23.2533 38 28.5C38 33.7467 33.7467 38 28.5 38C23.2533 38 19 33.7467 19 28.5Z" fill="#1ABCFE" />
    <path d="M0 47.5C0 42.2533 4.25329 38 9.5 38H19V47.5C19 52.7467 14.7467 57 9.5 57C4.25329 57 0 52.7467 0 47.5Z" fill="#0ACF83" />
    <path d="M19 0V19H28.5C33.7467 19 38 14.7467 38 9.5C38 4.25329 33.7467 0 28.5 0H19Z" fill="#FF7262" />
    <path d="M0 9.5C0 14.7467 4.25329 19 9.5 19H19V0H9.5C4.25329 0 0 4.25329 0 9.5Z" fill="#F24E1E" />
    <path d="M0 28.5C0 33.7467 4.25329 38 9.5 38H19V19H9.5C4.25329 19 0 23.2533 0 28.5Z" fill="#A259FF" />
  </svg>
);

const PdfLogo: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <div className={`flex items-center justify-center rounded bg-red-100 dark:bg-red-950/60 text-red-600 dark:text-red-400 font-black text-[9px] px-1 py-0.5 tracking-tight border border-red-200/80 dark:border-red-900/50 ${className}`}>
    PDF
  </div>
);

// ─── Format Text with @Mention Highlights ────────────────────────────────────
const FormattedText: React.FC<{ text: string; membersMap?: Record<string, string> }> = ({ text, membersMap: providedMembersMap }) => {
  const { members = [] } = useWorkspace() || {};

  const resolvedMembersMap = useMemo(() => {
    if (providedMembersMap) return providedMembersMap;

    const map: Record<string, string> = {};
    members.forEach((m) => {
      if (m?.user?.id && m?.user?.displayName) {
        map[m.user.id] = m.user.displayName;
      }
    });
    return map;
  }, [members, providedMembersMap]);
  const clean = text.replace(/^[*_~`]+|[*_~`]+$/g, '').trim();
  const segments = parseContentSegments(clean, resolvedMembersMap);

  return (
    <>
      {segments.map((seg, i) => {
        if (seg.type === 'mention') {
          return (
            <span key={i} className="font-semibold text-purple-600 dark:text-purple-400">
              {seg.value}
            </span>
          );
        }
        return <span key={i}>{seg.value}</span>;
      })}
    </>
  );
};

// ─── Node & Styling Resolver ─────────────────────────────────────────────────
function getActivityNodeConfig(type: ActivityEventType) {
  switch (type) {
    case 'MENTION':
      return {
        typeIcon: <AtSign className="h-3.5 w-3.5" />,
        nodeClass: 'bg-amber-50 text-amber-600 dark:bg-amber-950/80 dark:text-amber-400 border-amber-200/60 dark:border-amber-900/40',
        isDm: false
      };
    case 'REPLY':
      return {
        typeIcon: <Reply className="h-3.5 w-3.5" />,
        nodeClass: 'bg-pink-50 text-pink-600 dark:bg-pink-950/80 dark:text-pink-400 border-pink-200/60 dark:border-pink-900/40',
        isDm: false
      };
    case 'REACTION':
      return {
        typeIcon: <Smile className="h-3.5 w-3.5" />,
        nodeClass: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/80 dark:text-emerald-400 border-emerald-200/60 dark:border-emerald-900/40',
        isDm: false
      };
    case 'FILE_SHARED':
      return {
        typeIcon: <Paperclip className="h-3.5 w-3.5" />,
        nodeClass: 'bg-blue-50 text-blue-600 dark:bg-blue-950/80 dark:text-blue-400 border-blue-200/60 dark:border-blue-900/40',
        isDm: false
      };
    case 'DM':
      return {
        typeIcon: <Send className="h-3.5 w-3.5" />,
        nodeClass: 'bg-teal-50 text-teal-600 dark:bg-teal-950/80 dark:text-teal-400 border-teal-200/60 dark:border-teal-900/40',
        isDm: true
      };
    case 'MEMBER_ADDED':
      return {
        typeIcon: <UserPlus className="h-3.5 w-3.5" />,
        nodeClass: 'bg-rose-50 text-rose-600 dark:bg-rose-950/80 dark:text-rose-400 border-rose-200/60 dark:border-rose-900/40',
        isDm: false
      };
    case 'EMERGENCY_ALERT':
      return {
        typeIcon: <AlertTriangle className="h-3.5 w-3.5" />,
        nodeClass: 'bg-red-50 text-red-600 dark:bg-red-950/80 dark:text-red-400 border-red-200/60 dark:border-red-900/40',
        isDm: false
      };
    case 'MESSAGE':
    default:
      return {
        typeIcon: <MessageSquare className="h-3.5 w-3.5" />,
        nodeClass: 'bg-purple-50 text-purple-600 dark:bg-purple-950/80 dark:text-purple-400 border-purple-200/60 dark:border-purple-900/40',
        isDm: false
      };
  }
}

// ─── Main Activity View Component ────────────────────────────────────────────
export const ActivityView: React.FC<ActivityViewProps> = ({ onNavigateToContext }) => {
  const { currentWorkspace, members } = useWorkspace();
  const { selectChannel, selectDm } = useChat();
  const { user: currentUser } = useAuth();
  const { t } = useLanguage();

  // Build UUID->displayName map for mention resolution across the activity feed
  const membersMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const m of members) {
      if (m.user?.id && m.user?.displayName) {
        map[m.user.id] = m.user.displayName;
      }
    }
    return map;
  }, [members]);

  const [activities, setActivities] = useState<FeedActivityItem[]>([]);
  const [activeFilter, setActiveFilter] = useState<ActivityFilter>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Global shortcut (Cmd+K / Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // ─── Comprehensive Workspace Feed Aggregation ──────────────────────────────
  const fetchWorkspaceActivity = useCallback(async () => {
    if (!currentWorkspace?.id) return;
    try {
      setIsLoading(true);

      // Concurrently fetch channels, conversations, notifications, and files safely
      const [channelsList, dmsList, notificationsRes, filesRes] = await Promise.all([
        api.channels.listForWorkspace(currentWorkspace.id).catch(err => {
          console.warn('Channels load warning:', err);
          return [] as Channel[];
        }),
        api.directConversations.list(currentWorkspace.id).catch(err => {
          console.warn('DMs load warning:', err);
          return [] as DirectConversation[];
        }),
        api.notifications.list(currentWorkspace.id, { limit: 50 }).catch(err => {
          console.warn('Notifications load warning:', err);
          return { data: [] as Notification[] };
        }),
        api.files.list(currentWorkspace.id, { size: 30 }).catch(err => {
          console.warn('Files load warning:', err);
          return { data: [] as WorkspaceFile[] };
        }),
      ]);

      const safeChannels = Array.isArray(channelsList) ? channelsList : [];
      const safeDms = Array.isArray(dmsList) ? dmsList : [];
      const notifs: Notification[] = notificationsRes?.data || [];
      const files: WorkspaceFile[] = filesRes?.data || [];

      const items: FeedActivityItem[] = [];

      // 1. Map Notifications
      for (const n of notifs) {
        try {
          const typeStr = String(n.type || '').toUpperCase();
          let itemType: ActivityEventType = 'GENERIC';
          let action = 'sent an update';

          if (typeStr === 'MENTION') {
            itemType = 'MENTION';
            action = 'mentioned you in';
          } else if (typeStr === 'REPLY') {
            itemType = 'REPLY';
            action = 'replied in';
          } else if (typeStr === 'REACTION') {
            itemType = 'REACTION';
            action = 'reacted to your message in';
          } else if (typeStr === 'FILE_SHARED') {
            itemType = 'FILE_SHARED';
            action = 'shared a file in';
          } else if (typeStr === 'DM') {
            itemType = 'DM';
            action = 'sent you a direct message';
          } else if (typeStr === 'MEMBER_ADDED' || typeStr === 'WORKSPACE_INVITED') {
            itemType = 'MEMBER_ADDED';
            action = 'added';
          } else if (typeStr === 'EMERGENCY_ALERT') {
            itemType = 'EMERGENCY_ALERT';
            action = 'broadcasted an emergency alert';
          }

          const channel = safeChannels.find(c => c.id === n.referenceId);
          const channelName = channel?.name || 'general';

          items.push({
            id: `notif-${n.id}`,
            notificationId: n.id,
            type: itemType,
            actor: {
              id: n.actor?.id || n.id,
              displayName: n.actor?.displayName || 'Team Member',
              avatarUrl: n.actor?.avatarUrl
            },
            actionText: action,
            channelName,
            channelId: channel?.id || n.referenceId,
            content: n.contentPreview || undefined,
            isRead: n.isRead,
            createdAt: n.createdAt || new Date().toISOString()
          });
        } catch {
          // ignore single item error
        }
      }

      // 2. Fetch Channel Messages for Workspace Channels
      if (safeChannels.length > 0) {
        const channelMessagePromises = safeChannels.map(ch =>
          api.messages.list(ch.id, { limit: 20 })
            .then(res => ({ channel: ch, messages: res?.data || [] }))
            .catch(() => ({ channel: ch, messages: [] as Message[] }))
        );

        const channelResults = await Promise.all(channelMessagePromises);

        for (const { channel, messages } of channelResults) {
          for (const m of messages) {
            try {
              if (!m || (!m.content && (!m.reactions || m.reactions.length === 0))) continue;

              const isReply = !!m.parentId;
              const isMention = m.content?.includes('@');

              const actorName = m.sender?.displayName || 'Team Member';
              const actorId = m.sender?.id || m.id;
              const actorAvatar = m.sender?.avatarUrl;

              // Message / Reply / Mention Activity
              items.push({
                id: `msg-${m.id}`,
                type: isReply ? 'REPLY' : isMention ? 'MENTION' : 'MESSAGE',
                actor: {
                  id: actorId,
                  displayName: actorName,
                  avatarUrl: actorAvatar
                },
                actionText: isReply ? 'replied in' : isMention ? 'mentioned you in' : 'sent a message in',
                channelName: channel.name,
                channelId: channel.id,
                content: m.content,
                isRead: true,
                createdAt: m.createdAt || new Date().toISOString()
              });

              // Reactions on Messages
              if (m.reactions && m.reactions.length > 0) {
                for (const r of m.reactions) {
                  items.push({
                    id: `reaction-${m.id}-${r.emoji}`,
                    type: 'REACTION',
                    actor: {
                      id: actorId,
                      displayName: actorName,
                      avatarUrl: actorAvatar
                    },
                    actionText: 'reacted to your message in',
                    channelName: channel.name,
                    channelId: channel.id,
                    reactionEmoji: r.emoji,
                    isRead: true,
                    createdAt: m.updatedAt || m.createdAt || new Date().toISOString()
                  });
                }
              }
            } catch {
              // ignore single message error
            }
          }
        }
      }

      // 3. Fetch Direct Messages for Conversations
      if (safeDms.length > 0) {
        const dmMessagePromises = safeDms.map(conv =>
          api.directConversations.listMessages(conv.id, { limit: 20 })
            .then(res => ({ conv, messages: res?.data || [] }))
            .catch(() => ({ conv, messages: [] as DirectMessage[] }))
        );

        const dmResults = await Promise.all(dmMessagePromises);

        for (const { conv, messages } of dmResults) {
          const otherMember = conv.members?.find(m => m.id !== currentUser?.id);
          const participantName = otherMember?.displayName || conv.title || 'Direct Message';

          for (const dm of messages) {
            try {
              if (!dm || !dm.content) continue;

              const dmActorName = dm.sender?.displayName || participantName || 'Team Member';
              const dmActorId = dm.sender?.id || dm.id;
              const dmActorAvatar = dm.sender?.avatarUrl;

              items.push({
                id: `dm-msg-${dm.id}`,
                type: 'DM',
                actor: {
                  id: dmActorId,
                  displayName: dmActorName,
                  avatarUrl: dmActorAvatar
                },
                actionText: 'started a direct message with',
                participantName,
                conversationId: conv.id,
                content: dm.content,
                isRead: true,
                createdAt: dm.createdAt || new Date().toISOString()
              });

              // DM Reactions
              if (dm.reactions && dm.reactions.length > 0) {
                for (const r of dm.reactions) {
                  items.push({
                    id: `dm-reaction-${dm.id}-${r.emoji}`,
                    type: 'REACTION',
                    actor: {
                      id: dmActorId,
                      displayName: dmActorName,
                      avatarUrl: dmActorAvatar
                    },
                    actionText: 'reacted to your message in direct message with',
                    participantName,
                    conversationId: conv.id,
                    reactionEmoji: r.emoji,
                    isRead: true,
                    createdAt: dm.createdAt || new Date().toISOString()
                  });
                }
              }
            } catch {
              // ignore single DM error
            }
          }
        }
      }

      // 4. Map Workspace Files
      for (const f of files) {
        try {
          const fileChannel = safeChannels.find(c => c.id === f.channelId);
          const channelName = fileChannel?.name || 'resources';
          const fileInfo = getFileDetails(f.fileName, f.fileSize);

          items.push({
            id: `file-${f.id}`,
            type: 'FILE_SHARED',
            actor: {
              id: f.uploader?.id || f.id,
              displayName: f.uploader?.displayName || 'Team Member',
              avatarUrl: f.uploader?.avatarUrl
            },
            actionText: 'shared a file in',
            channelName,
            channelId: f.channelId,
            fileInfo,
            isRead: true,
            createdAt: f.createdAt || new Date().toISOString()
          });
        } catch {
          // ignore single file error
        }
      }

      // 5. Deduplicate and Sort Chronologically (Newest first)
      const uniqueMap = new Map<string, FeedActivityItem>();
      for (const it of items) {
        if (!uniqueMap.has(it.id)) {
          uniqueMap.set(it.id, it);
        }
      }

      const sorted = Array.from(uniqueMap.values()).sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

      setActivities(sorted);
    } catch (err) {
      console.error('Failed to load activity stream', err);
    } finally {
      setIsLoading(false);
    }
  }, [currentWorkspace?.id, currentUser?.id]);

  useEffect(() => {
    fetchWorkspaceActivity();
  }, [fetchWorkspaceActivity]);

  const handleMarkAllRead = async () => {
    if (!currentWorkspace?.id) return;
    try {
      await api.notifications.markAllRead(currentWorkspace.id);
      setActivities(prev => prev.map(n => ({ ...n, isRead: true })));
    } catch (err) {
      console.error('Failed to mark all read', err);
    }
  };

  const handleItemClick = async (item: FeedActivityItem) => {
    if (!item.isRead && item.notificationId) {
      api.notifications.markRead(item.notificationId).catch(() => { });
      setActivities(prev =>
        prev.map(n => (n.id === item.id ? { ...n, isRead: true } : n))
      );
    }

    // Contextual navigation
    if (item.type === 'DM' || item.conversationId) {
      if (item.conversationId) {
        try {
          const conv = await api.directConversations.get(item.conversationId);
          if (conv) selectDm(conv);
        } catch {
          // ignore
        }
      }
      if (onNavigateToContext) onNavigateToContext('dm', item.conversationId);
    } else if (item.type === 'FILE_SHARED') {
      if (onNavigateToContext) onNavigateToContext('files');
    } else if (item.channelId) {
      try {
        const ch = await api.channels.get(item.channelId);
        if (ch) selectChannel(ch);
      } catch {
        // ignore
      }
      if (onNavigateToContext) onNavigateToContext('channel', item.channelId);
    }
  };

  // Filter & Search
  const filteredActivities = useMemo(() => {
    let list = activities;

    if (activeFilter === 'MENTIONS') {
      list = list.filter(n => n.type === 'MENTION' || (n.content && n.content.includes('@')));
    } else if (activeFilter === 'REPLIES') {
      list = list.filter(n => n.type === 'REPLY' || n.actionText.includes('replied'));
    } else if (activeFilter === 'REACTIONS') {
      list = list.filter(n => n.type === 'REACTION' || !!n.reactionEmoji);
    } else if (activeFilter === 'DMS') {
      list = list.filter(n => n.type === 'DM' || !!n.conversationId || n.actionText.includes('direct message'));
    } else if (activeFilter === 'FILES') {
      list = list.filter(n => n.type === 'FILE_SHARED' || !!n.fileInfo);
    }

    if (!searchQuery.trim()) return list;
    const q = searchQuery.toLowerCase();
    return list.filter(n => {
      const name = n.actor.displayName.toLowerCase();
      const content = n.content?.toLowerCase() || '';
      const channel = n.channelName?.toLowerCase() || '';
      const participant = n.participantName?.toLowerCase() || '';
      const file = n.fileInfo?.fileName.toLowerCase() || '';
      return (
        name.includes(q) ||
        content.includes(q) ||
        channel.includes(q) ||
        participant.includes(q) ||
        file.includes(q)
      );
    });
  }, [activities, activeFilter, searchQuery]);

  // Group by Today, Yesterday, Earlier
  const groupedActivities = useMemo(() => {
    const today: FeedActivityItem[] = [];
    const yesterday: FeedActivityItem[] = [];
    const earlier: { dateKey: string; items: FeedActivityItem[] }[] = [];

    const now = new Date();
    const todayStr = now.toDateString();

    const y = new Date();
    y.setDate(y.getDate() - 1);
    const yesterdayStr = y.toDateString();

    const earlierMap = new Map<string, FeedActivityItem[]>();

    for (const item of filteredActivities) {
      const d = new Date(item.createdAt);
      const dateStr = d.toDateString();
      if (dateStr === todayStr) {
        today.push(item);
      } else if (dateStr === yesterdayStr) {
        yesterday.push(item);
      } else {
        const key = d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
        if (!earlierMap.has(key)) {
          earlierMap.set(key, []);
        }
        earlierMap.get(key)!.push(item);
      }
    }

    earlierMap.forEach((items, dateKey) => {
      earlier.push({ dateKey, items });
    });

    return { today, yesterday, earlier };
  }, [filteredActivities]);

  const formatTimestamp = (isoString: string) => {
    const d = new Date(isoString);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  const filterTabs: { id: ActivityFilter; label: string; icon: React.ReactNode }[] = [
    { id: 'ALL', label: t('activity.all'), icon: <LayoutGrid className="h-3.5 w-3.5" /> },
    { id: 'MENTIONS', label: t('activity.mentions'), icon: <AtSign className="h-3.5 w-3.5" /> },
    { id: 'REPLIES', label: t('activity.replies'), icon: <MessageSquare className="h-3.5 w-3.5" /> },
    { id: 'REACTIONS', label: t('activity.reactions'), icon: <Smile className="h-3.5 w-3.5" /> },
    { id: 'DMS', label: t('sidebar.directMessages'), icon: <Send className="h-3.5 w-3.5" /> },
    { id: 'FILES', label: t('activity.files'), icon: <FileText className="h-3.5 w-3.5" /> },
  ];

  const getEmptyStateText = () => {
    if (searchQuery) {
      return {
        title: 'No matching activity',
        desc: `No activity matched "${searchQuery}". Try another keyword or clear the search filter.`
      };
    }
    switch (activeFilter) {
      case 'MENTIONS':
        return {
          title: 'No mentions yet',
          desc: "When teammates mention you with @name in a channel, they will appear here."
        };
      case 'REPLIES':
        return {
          title: 'No replies yet',
          desc: "When someone replies to your messages or threads, you'll see them right here."
        };
      case 'REACTIONS':
        return {
          title: 'No reactions yet',
          desc: "When teammates react to your messages with emojis, you'll see them here."
        };
      case 'DMS':
        return {
          title: 'No direct messages yet',
          desc: "Direct messages and private conversations with colleagues will appear here."
        };
      case 'FILES':
        return {
          title: 'No files shared yet',
          desc: "Files and documents uploaded to channels or direct messages will appear here."
        };
      case 'ALL':
      default:
        return {
          title: t('activity.emptyTitle'),
          desc: t('activity.emptyDesc')
        };
    }
  };

  const renderSection = (title: string, items: FeedActivityItem[]) => {
    if (items.length === 0) return null;

    return (
      <div className="mb-8 last:mb-2">
        <h3 className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider mb-4 px-1 select-none">
          {title}
        </h3>

        <div className="relative pl-0">
          <div className="absolute left-[15px] top-6 bottom-6 w-[2px] bg-border -z-0" />

          <div className="space-y-3.5">
            {items.map((item) => {
              const config = getActivityNodeConfig(item.type);
              const time = formatTimestamp(item.createdAt);

              return (
                <div
                  key={item.id}
                  onClick={() => handleItemClick(item)}
                  className="relative flex items-start gap-3.5 sm:gap-4 group cursor-pointer"
                >
                  {/* Spine Node Badge */}
                  <div
                    className={`w-8 h-8 rounded-full border-2 border-background flex items-center justify-center text-xs flex-shrink-0 z-10 shadow-xs mt-3.5 transition-transform duration-200 group-hover:scale-110 ${config.nodeClass}`}
                  >
                    {config.typeIcon}
                  </div>

                  {/* Activity Card */}
                  <div
                    className={`flex-1 rounded-2xl p-4 sm:p-4.5 border transition-all duration-200 flex items-start gap-3.5 relative min-w-0 ${!item.isRead
                        ? 'bg-card border-primary/40 shadow-sm ring-1 ring-primary/15'
                        : 'bg-card border-border hover:border-primary/40 shadow-xs hover:shadow-md'
                      }`}
                  >
                    {/* User Avatar */}
                    <div className="relative flex-shrink-0 mt-0.5">
                      <UserAvatar
                        userId={item.actor.id}
                        displayName={item.actor.displayName}
                        avatarUrl={item.actor.avatarUrl}
                        size="sm"
                        showStatus
                        className="shadow-xs shrink-0"
                      />
                    </div>

                    {/* Activity Content */}
                    <div className="flex-1 min-w-0">
                      {/* Top Header line */}
                      <div className="flex items-center flex-wrap gap-x-1.5 gap-y-1 text-xs sm:text-[13px] leading-snug min-w-0 pr-2">
                        <span className="font-bold text-foreground group-hover:text-primary transition-colors">
                          {item.actor.displayName}
                        </span>
                        <span className="text-muted-foreground font-normal">
                          {item.actionText}
                        </span>

                        {item.channelName && <ChannelBadge name={item.channelName} />}
                        {item.participantName && (
                          <span className="font-bold text-xs sm:text-[13px] text-primary">
                            {item.participantName}
                          </span>
                        )}
                      </div>

                      {/* Content Details */}
                      {item.type === 'MESSAGE' && item.content && (
                        <div className="mt-2 text-xs text-foreground/90 leading-relaxed bg-muted/40 border border-border rounded-xl p-3 shadow-xs">
                          <FormattedText text={item.content} membersMap={membersMap} />
                        </div>
                      )}

                      {item.type === 'REPLY' && item.content && (
                        <div className="mt-1.5 text-xs text-foreground/90 leading-relaxed">
                          <FormattedText text={item.content} membersMap={membersMap} />
                        </div>
                      )}

                      {item.type === 'MENTION' && item.content && (
                        <div className="mt-1.5 text-xs text-foreground/90 leading-relaxed bg-amber-500/10 border border-amber-500/20 rounded-xl p-2.5">
                          <FormattedText text={item.content} membersMap={membersMap} />
                        </div>
                      )}

                      {item.type === 'REACTION' && (
                        <div className="mt-2">
                          <span className="inline-flex items-center gap-1.5 bg-amber-500/10 border border-amber-500/30 rounded-full px-3 py-1 text-xs font-semibold text-amber-600 dark:text-amber-400 shadow-xs">
                            <span className="text-sm">{item.reactionEmoji || '👍'}</span>
                            <span>1</span>
                          </span>
                        </div>
                      )}

                      {item.type === 'FILE_SHARED' && item.fileInfo && (
                        <div className="mt-2.5 inline-flex items-center gap-3 bg-muted/40 border border-border rounded-xl p-2.5 px-3.5 shadow-xs max-w-full hover:border-primary/40 transition-colors">
                          {item.fileInfo.iconType === 'figma' ? (
                            <FigmaLogo className="w-4 h-4 flex-shrink-0" />
                          ) : item.fileInfo.iconType === 'pdf' ? (
                            <PdfLogo className="flex-shrink-0" />
                          ) : item.fileInfo.iconType === 'image' ? (
                            <ImageIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                          ) : item.fileInfo.iconType === 'code' ? (
                            <FileCode className="w-4 h-4 text-primary flex-shrink-0" />
                          ) : item.fileInfo.iconType === 'video' ? (
                            <Film className="w-4 h-4 text-purple-500 flex-shrink-0" />
                          ) : item.fileInfo.iconType === 'audio' ? (
                            <Music className="w-4 h-4 text-pink-500 flex-shrink-0" />
                          ) : item.fileInfo.iconType === 'archive' ? (
                            <Archive className="w-4 h-4 text-amber-500 flex-shrink-0" />
                          ) : (
                            <FileText className="w-4 h-4 text-blue-500 flex-shrink-0" />
                          )}

                          <span className="text-xs font-semibold text-foreground truncate max-w-[200px] sm:max-w-[280px]">
                            {item.fileInfo.fileName}
                          </span>

                          <span className="text-[11px] text-muted-foreground font-medium ml-1 flex-shrink-0">
                            {item.fileInfo.fileSize}
                          </span>
                        </div>
                      )}

                      {item.type === 'DM' && item.content && (
                        <p className="mt-1 text-xs text-muted-foreground leading-relaxed truncate">
                          {item.content}
                        </p>
                      )}

                      {item.type === 'EMERGENCY_ALERT' && (
                        <div className="mt-2 text-xs font-medium text-destructive leading-relaxed bg-destructive/10 border border-destructive/20 rounded-xl p-3 shadow-xs">
                          {item.content || 'Emergency broadcast sent to workspace'}
                        </div>
                      )}
                    </div>

                    {/* Right Info */}
                    <div className="flex items-center gap-2 flex-shrink-0 select-none pt-0.5 ml-2">
                      <span className="text-xs text-muted-foreground font-medium tabular-nums">
                        {time}
                      </span>

                      {!item.isRead ? (
                        <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0 animate-pulse" />
                      ) : config.isDm ? (
                        <ArrowRight className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                      ) : (
                        <ArrowRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 group-hover:text-primary transition-all flex-shrink-0" />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const hasAnyActivities =
    groupedActivities.today.length > 0 ||
    groupedActivities.yesterday.length > 0 ||
    groupedActivities.earlier.length > 0;

  const unreadCount = activities.filter(n => !n.isRead).length;
  const emptyState = getEmptyStateText();

  return (
    <div className="flex-1 flex flex-col h-full bg-background text-foreground overflow-hidden">
      {/* 🧭 PAGE HEADER */}
      <header className="h-18 min-h-[72px] border-b border-border bg-card/60 backdrop-blur-md px-6 sm:px-8 flex items-center justify-between gap-4 select-none flex-shrink-0 z-20">
        <div className="flex items-center gap-3.5 min-w-0">
          <div className="w-10 h-10 rounded-2xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 shadow-xs border border-primary/20">
            <Bell className="h-5 w-5" />
          </div>

          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="font-bold text-foreground text-lg sm:text-xl tracking-tight">
                {t('activity.title')}
              </h1>
              {unreadCount > 0 && (
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-primary text-primary-foreground shadow-xs">
                  {unreadCount}
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground font-normal truncate mt-0.5">
              {t('activity.subtitle')}
            </p>
          </div>
        </div>

        {/* Mark All Read */}
        <div className="flex items-center gap-2.5">
          <button
            onClick={handleMarkAllRead}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-card hover:bg-muted text-foreground text-xs font-semibold border border-border shadow-xs transition-all active:scale-95"
          >
            <Check className="h-3.5 w-3.5 text-emerald-500 stroke-[2.5]" />
            <span>{t('activity.markAllRead')}</span>
          </button>
        </div>
      </header>

      {/* 🔎 ACTIVITY FILTER BAR & SEARCH */}
      <div className="px-6 sm:px-8 py-3.5 border-b border-border bg-card/40 flex flex-wrap items-center justify-between gap-3 select-none flex-shrink-0">
        {/* Filter Pills */}
        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar py-0.5">
          {filterTabs.map((tab) => {
            const isActive = activeFilter === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveFilter(tab.id)}
                className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all select-none shadow-xs ${isActive
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'bg-card hover:bg-muted text-muted-foreground hover:text-foreground border border-border'
                  }`}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Search Input */}
        <div className="relative flex items-center bg-background rounded-xl px-3 py-1.5 w-full sm:w-64 border border-border shadow-xs focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/10 transition-all">
          <Search className="h-3.5 w-3.5 text-muted-foreground mr-2 flex-shrink-0" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder={t('activity.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="bg-transparent border-none focus:outline-none text-xs w-full placeholder:text-muted-foreground text-foreground"
          />
          {searchQuery ? (
            <button
              onClick={() => setSearchQuery('')}
              className="text-muted-foreground hover:text-foreground p-0.5"
            >
              <X className="h-3 w-3" />
            </button>
          ) : (
            <kbd className="hidden sm:inline-flex items-center gap-0.5 text-[10px] font-semibold text-muted-foreground bg-muted px-1.5 py-0.5 rounded border border-border select-none">
              ⌘ K
            </kbd>
          )}
        </div>
      </div>

      {/* 🕒 TIMELINE FEED */}
      <div className="flex-1 overflow-y-auto p-6 sm:p-8 w-full">
        {isLoading ? (
          <div className="h-64 flex flex-col items-center justify-center gap-3 text-muted-foreground text-xs">
            <div className="w-7 h-7 rounded-full border-2 border-primary border-t-transparent animate-spin" />
            <span>{t('common.loading')}</span>
          </div>
        ) : !hasAnyActivities ? (
          <div className="h-full min-h-[380px] flex flex-col items-center justify-center text-center p-8">
            <div className="w-16 h-16 rounded-3xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-4 text-primary shadow-sm">
              <Inbox className="h-8 w-8" />
            </div>
            <h3 className="text-base font-bold text-foreground mb-1.5">
              {emptyState.title}
            </h3>
            <p className="text-xs text-muted-foreground max-w-sm leading-relaxed">
              {emptyState.desc}
            </p>
          </div>
        ) : (
          <>
            {renderSection(t('common.today'), groupedActivities.today)}
            {renderSection(t('common.yesterday'), groupedActivities.yesterday)}
            {groupedActivities.earlier.map(({ dateKey, items }) => (
              <React.Fragment key={dateKey}>
                {renderSection(dateKey, items)}
              </React.Fragment>
            ))}
          </>
        )}
      </div>
    </div>
  );
};
